import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardModule, GridModule, ProgressModule, SpinnerModule, BadgeModule, AlertModule } from '@coreui/angular-pro';
import { TranslatePipe, TranslationModule } from 'i18n';
import { Logger } from 'shared';

interface WpcpAccount {
  active_points: number;
  auto_points: number;
  manual_points: number;
  deactivated_points: number;
  regular_max: number;
  absolute_max: number;
  effective_max: number;
  special_release: boolean;
}
interface WpcpPayout {
  settlement_key: string;
  kind: string;
  currency: string;
  final_credit: number;
  credited_at: string;
}
interface WpcpNotification { id: number; kind: string; title: string; body: string; created_at: string }
interface WpcpDashboard {
  account: WpcpAccount;
  categories?: { key: string; name: string }[];
  compensation?: {
    active_direct_count: number; direct_points: number; to_next_direct_point: number | null;
    pack_builder_count: number; pack_builder_points: number; to_next_builder_bonus: number | null;
    leadership: {
      organization: number; rank: string | null; weekly: number;
      next_rank: string | null; next_threshold: number | null; to_next_rank: number | null;
    };
  };
  last_settlement?: {
    settlement_key: string; member_pool_points: number; pool_payout: number;
    leadership_rank: string | null; leadership_payout: number;
    pack_hero_level: number; pack_hero_payout: number;
    direct_compensation: number; final_weekly_payout: number;
  } | null;
  history?: { id: number; category_key: string; rule_code: string; points: number; source: string; state: string; reason: string; awarded_at: string }[];
  pool_payouts?: { last_community?: WpcpPayout; last_legacy?: WpcpPayout; history?: WpcpPayout[] };
  notifications?: WpcpNotification[];
}

// Community Points widget for the user dashboard (spec v3.0 ch.24/25). Self-contained;
// fails silently (renders nothing) on backends without the WPCP endpoints.
@Component({
  selector: 'app-wpcp-dashboard-widget',
  standalone: true,
  imports: [CommonModule, CardModule, GridModule, ProgressModule, SpinnerModule, BadgeModule, AlertModule, TranslationModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <c-card class="mb-4" *ngIf="!failed()">
      <c-card-header>
        <strong>{{ 'wpcp.title' | translate }}</strong>
      </c-card-header>
      <c-card-body>
        <div *ngIf="loading()" class="text-center py-3"><c-spinner size="sm" /></div>

        <div *ngIf="!loading() && data() as d">
          <c-alert *ngFor="let n of d.notifications || []" color="success" [dismissible]="true"
                   (visibleChange)="dismiss(n)">
            <strong>{{ n.title }}</strong> — {{ n.body }}
          </c-alert>

          <!-- What the member can act on. A bare point total says nothing about
               what to do next; the gap to the next full group does. -->
          <c-row class="mb-3" *ngIf="d.compensation as c">
            <c-col [md]="3" class="mb-2">
              <div class="fs-3 fw-bold">{{ d.account.active_points }}</div>
              <div class="text-medium-emphasis small">{{ 'wpcp.active_points' | translate }}</div>
            </c-col>
            <c-col [md]="3" class="mb-2">
              <div class="fs-5">{{ c.active_direct_count }}<span class="fs-6 text-medium-emphasis"> → {{ c.direct_points }} {{ 'wpcp.widget.points_abbr' | translate }}</span></div>
              <div class="text-medium-emphasis small">
                {{ 'wpcp.widget.active_directs' | translate }}
                <span *ngIf="c.to_next_direct_point"> · {{ 'wpcp.widget.to_next_direct_point' | translate: { count: c.to_next_direct_point } }}</span>
              </div>
            </c-col>
            <c-col [md]="3" class="mb-2">
              <div class="fs-5">{{ c.pack_builder_count }}<span class="fs-6 text-medium-emphasis"> → {{ c.pack_builder_points }} {{ 'wpcp.widget.points_abbr' | translate }}</span></div>
              <div class="text-medium-emphasis small">
                {{ 'wpcp.widget.pack_builders' | translate }}
                <span *ngIf="c.to_next_builder_bonus"> · {{ 'wpcp.widget.to_next_builder_bonus' | translate: { count: c.to_next_builder_bonus } }}</span>
              </div>
            </c-col>
            <c-col [md]="3" class="mb-2">
              <div class="fs-5">{{ d.account.manual_points }}</div>
              <div class="text-medium-emphasis small">{{ 'wpcp.manual_points' | translate }}</div>
            </c-col>
          </c-row>

          <c-progress class="mb-1" [value]="progress(d.account)" />
          <div class="d-flex justify-content-between small text-medium-emphasis mb-3">
            <span>0</span>
            <span>{{ 'wpcp.widget.auto_max' | translate: { count: d.account.regular_max } }}</span>
            <span>{{ 'wpcp.widget.special_max' | translate: { special: d.account.absolute_max - d.account.regular_max, max: d.account.absolute_max } }}</span>
          </div>

          <!-- Leadership is a different currency: organization size at unlimited
               depth, not points. Shown separately so the two are not confused. -->
          <div class="mb-3 p-2 border rounded" *ngIf="d.compensation?.leadership as l">
            <div class="d-flex flex-wrap gap-3 align-items-baseline">
              <div>
                <span class="fw-semibold">{{ l.organization }}</span>
                <span class="text-medium-emphasis small"> {{ 'wpcp.widget.org_active_members' | translate }}</span>
              </div>
              <div *ngIf="l.rank">
                <c-badge color="info">{{ l.rank }}</c-badge>
                <span class="small text-medium-emphasis"> · {{ l.weekly | number:'1.2-2' }} {{ 'wpcp.widget.per_week' | translate }}</span>
              </div>
              <div *ngIf="l.next_rank" class="small text-medium-emphasis">
                {{ 'wpcp.widget.to_next_rank' | translate: { count: l.to_next_rank, rank: l.next_rank } }}
              </div>
            </div>
          </div>

          <!-- The authoritative record of what was actually paid, straight from the
               settlement — not recomputed, because the qualification that counts is
               the one at the Friday snapshot. -->
          <div class="mb-3" *ngIf="d.last_settlement as ls">
            <div class="fw-semibold small mb-1">{{ 'wpcp.widget.last_settlement' | translate: { key: ls.settlement_key } }}</div>
            <div class="small">
              <span *ngIf="ls.pool_payout > 0">{{ 'wpcp.widget.settlement_points' | translate }} {{ ls.pool_payout | number:'1.2-2' }}</span>
              <span *ngIf="ls.leadership_payout > 0"> · {{ 'wpcp.widget.settlement_leadership' | translate }} {{ ls.leadership_payout | number:'1.2-2' }}</span>
              <span *ngIf="ls.pack_hero_payout > 0"> · {{ 'wpcp.widget.settlement_pack_hero' | translate }} {{ ls.pack_hero_payout | number:'1.2-2' }}</span>
              <span class="fw-semibold"> · {{ 'wpcp.widget.settlement_total' | translate }} {{ ls.final_weekly_payout | number:'1.2-2' }}</span>
            </div>
            <div class="small text-medium-emphasis" *ngIf="ls.direct_compensation > 0">
              {{ 'wpcp.widget.direct_compensation' | translate: { amount: (ls.direct_compensation | number:'1.2-2') } }}
            </div>
          </div>

          <div class="mb-2" *ngIf="d.pool_payouts?.last_community as lc">
            <span class="text-medium-emphasis small">{{ 'wpcp.last_community_payout' | translate }}:</span>
            {{ lc.final_credit }} {{ lc.currency }} <span class="small text-medium-emphasis">({{ lc.settlement_key }})</span>
          </div>
          <div class="mb-2" *ngIf="d.pool_payouts?.last_legacy as ll">
            <span class="text-medium-emphasis small">{{ 'wpcp.last_legacy_payout' | translate }}:</span>
            {{ ll.final_credit }} {{ ll.currency }} <span class="small text-medium-emphasis">({{ ll.settlement_key }})</span>
          </div>

          <div *ngIf="d.history?.length" class="mt-3">
            <div class="fw-semibold mb-2">{{ 'wpcp.history' | translate }}</div>
            <div class="small" *ngFor="let h of (d.history || []).slice(0, 8)">
              <span [class.text-decoration-line-through]="h.state !== 'active'">
                {{ h.points }} — {{ h.category_key }}<span *ngIf="h.rule_code">/{{ h.rule_code }}</span>
              </span>
              <span class="text-medium-emphasis"> · {{ h.awarded_at | date:'short' }}</span>
            </div>
          </div>
        </div>
      </c-card-body>
    </c-card>
  `
})
export class WpcpDashboardWidgetComponent {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('WpcpDashboardWidgetComponent');

  data = signal<WpcpDashboard | null>(null);
  loading = signal(true);
  failed = signal(false);

  constructor() {
    this.http.get<WpcpDashboard>('/wpcp/dashboard')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => { this.data.set(d); this.loading.set(false); },
        error: (e) => { this.logger.warn('WPCP dashboard unavailable', e); this.failed.set(true); this.loading.set(false); }
      });
  }

  progress(a: WpcpAccount): number {
    const max = a.absolute_max || 250;
    return Math.min(100, Math.round(((a.active_points || 0) / max) * 100));
  }

  dismiss(n: WpcpNotification) {
    this.http.post(`/wpcp/notifications/${n.id}/read`, {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => {}, error: (e) => this.logger.warn('dismiss failed', e) });
    const d = this.data();
    if (d) { d.notifications = (d.notifications || []).filter((x) => x.id !== n.id); this.data.set({ ...d }); }
  }
}
